import React from 'react';
import '../assets/Reports.css';
import Section from '../reports/Section';
import DeviceInfoTable from '../reports/DeviceInfoTable';
import CompactPieChart from '../reports/CompactPieChart';
import TimeSeriesChart from '../reports/TimeSeriesChart';
import HistogramChart from '../reports/HistogramChart';
import EventTimeSeriesChart from '../reports/EventTimeSeriesChart';
import StatsCard from '../reports/StatsCard';
import BootKpiStatsCard from '../reports/BootKpiStatsCard';
import HorizontalStatsCard from '../reports/HorizontalStatsCard';
import BootKpiTable from '../reports/BootKpiTable';
import AnomalyScatterChart from '../reports/AnomalyScatterChart';
import FilterBar from '../reports/FilterBar';

// Import hooks
import { useDeviceData, useDeviceSearch, isSectionLoading } from '../../hooks/useDeviceData';
import { useFilters } from '../../hooks/useFilters';
import { 
    ErrorState, 
    DeviceInfoSkeleton,
    PieChartSkeleton,
    TimeSeriesChartSkeleton,
    HistogramChartSkeleton,
    EventTimeSeriesSkeleton,
    AnomalyScatterSkeleton,
    StatsCardSkeleton
} from '../reports/LoadingSkeleton';

function DeviceView({ initialDeviceId, onInitialDeviceHandled }) {
    // Local state management for this subtab
    const [selectedTimeRange, setSelectedTimeRange] = React.useState('7d');
    const [isSearching, setIsSearching] = React.useState(false);
    const [currentDeviceId, setCurrentDeviceId] = React.useState('');
    const [reportsLoading, setReportsLoading] = React.useState(false);
    
    // Scroll-based filter bar visibility
    const [isFilterBarVisible, setIsFilterBarVisible] = React.useState(true);
    const lastScrollY = React.useRef(0);
    
    // Device search management
    const {
        searchTerm,
        recentDevices,
        handleSearchChange,
        addToRecentDevices
    } = useDeviceSearch();

    // Filter management hook
    const filterState = useFilters(selectedTimeRange);

    // Device data management with preloading
    const {
        data,
        loading,
        error,
        loadingStates,
        hasData,
        refresh,
        retrySection
    } = useDeviceData(currentDeviceId, selectedTimeRange);

    // Add device to recent list when data loads successfully
    React.useEffect(() => {
        if (hasData && data.metadata) {
            addToRecentDevices(currentDeviceId, data.metadata);
        }
    }, [hasData, data, currentDeviceId, addToRecentDevices]);

    // Add keyboard shortcut for refresh (Ctrl+R)
    React.useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'r' && currentDeviceId) {
                event.preventDefault();
                refresh();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [currentDeviceId, refresh]);

    // Reference to Device Information card for auto-expansion
    const deviceInfoCardRef = React.useRef(null);

    // Auto-expand Device Information section when metadata loads
    React.useEffect(() => {
        if (hasData && data.metadata && deviceInfoCardRef.current) {
            // Small delay to ensure smooth transition after loading completes
            setTimeout(() => {
                // Trigger expansion through the Card's internal state
                const cardHeader = deviceInfoCardRef.current.querySelector('.card-header');
                if (cardHeader && cardHeader.classList.contains('collapsed')) {
                    cardHeader.click(); // Trigger the Card's internal toggle
                }
            }, 100);
        }
    }, [hasData, data]);

    // Filter handlers
    const handleFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'makes':
                filterState.handleMakeChange(value);
                break;
            case 'models':
                filterState.handleModelChange(value);
                break;
            case 'operators':
                filterState.handleOperatorChange(value);
                break;
            default:
                break;
        }
    };

    // Search handler
    const handleSearch = (searchTerm) => {
        handleSearchChange(searchTerm);
        
        // Set searching state for immediate feedback
        setIsSearching(true);
        
        // Always set the device ID if there's a search term (validation is more flexible now)
        if (searchTerm && searchTerm.trim().length >= 3) {
            setCurrentDeviceId(searchTerm.trim());
        } else {
            setCurrentDeviceId('');
            setIsSearching(false);
        }
    };

    // Time range handler
    const handleTimeRangeChange = (range) => {
        setSelectedTimeRange(range);
        // Clear searching state when time range changes
        setIsSearching(false);
    };

    // Refresh handler
    const handleRefresh = () => {
        // Clear searching state when refresh is triggered
        setIsSearching(false);
        console.log('Refresh triggered for device:', currentDeviceId);
        refresh();
    };

    // Auto-search when navigated from fleet health device click
    React.useEffect(() => {
        if (initialDeviceId) {
            handleSearch(initialDeviceId);
            onInitialDeviceHandled && onInitialDeviceHandled();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDeviceId]);

    // Update loading state
    React.useEffect(() => {
        setReportsLoading(loading);
        // Clear searching state when loading completes
        if (!loading) {
            setIsSearching(false);
        }
    }, [loading]);

    // Scroll detection for filter bar visibility
    React.useEffect(() => {
        let scrollableElement = null;
        let cleanupListeners = [];
        
        const handleScroll = (event) => {
            const currentScrollY = event.target.scrollTop || window.scrollY;
            const scrollThreshold = 5; // Small threshold to prevent jitter
            
            // Only update if scroll difference is significant
            if (Math.abs(currentScrollY - lastScrollY.current) < scrollThreshold) return;
            
            if (currentScrollY > lastScrollY.current && currentScrollY > 20) {
                // Scrolling down - hide filter bar
                setIsFilterBarVisible(false);
            } else if (currentScrollY < lastScrollY.current) {
                // Scrolling up - show filter bar
                setIsFilterBarVisible(true);
            }
            
            lastScrollY.current = currentScrollY;
        };
        
        // Find the actual scrollable element
        setTimeout(() => {
            const allElements = document.querySelectorAll('*');
            
            for (let el of allElements) {
                if (el.scrollHeight > el.clientHeight) {
                    const computedStyle = window.getComputedStyle(el);
                    const overflowY = computedStyle.overflowY;
                    
                    if (overflowY === 'scroll' || overflowY === 'auto') {
                        scrollableElement = el;
                        break;
                    }
                }
            }
            
            // Add scroll listener to the found element or fallback to window
            if (scrollableElement) {
                scrollableElement.addEventListener('scroll', handleScroll, { passive: true });
                cleanupListeners.push(() => scrollableElement.removeEventListener('scroll', handleScroll));
            } else {
                // Fallback to window/document
                window.addEventListener('scroll', handleScroll, { passive: true });
                document.addEventListener('scroll', handleScroll, { passive: true });
                cleanupListeners.push(() => window.removeEventListener('scroll', handleScroll));
                cleanupListeners.push(() => document.removeEventListener('scroll', handleScroll));
            }
        }, 100);
        
        return () => {
            cleanupListeners.forEach(cleanup => cleanup());
        };
    }, []); // Empty dependency array to avoid re-running

    // Helper function to render device info table
    const renderDeviceInfoTable = () => {
        if (error) {
            return <ErrorState error={error} onRetry={refresh} />;
        }

        if (isSectionLoading('deviceInfo', loadingStates)) {
            return <DeviceInfoSkeleton />;
        }

        if (data?.metadata) {
            // Handle "no data" scenarios gracefully
            if (data.metadata.noData) {
                const noDataDeviceData = [
                    { label: "Device ID", value: data.metadata.device_id },
                    { label: "Make", value: "No data available" },
                    { label: "Model", value: "No data available" },
                    { label: "Operator", value: "No data available" },
                    { label: "OEM", value: "No data available" },
                    { label: "HW Type", value: "No data available" },
                    { label: "Chipset", value: "No data available" },
                    { label: "APSS", value: "No data available" },
                    { label: "MPSS", value: "No data available" },
                    { label: "Campaign ID", value: "No data available" }
                ];
                return <DeviceInfoTable deviceData={noDataDeviceData} />;
            }

            // Normal data display
            const deviceData = [
                { label: "System ID", value: data.metadata.device_id },
                { label: "Make", value: data.metadata.device_info_device_make },
                { label: "Model", value: data.metadata.device_info_device_model },
                { label: "Operator", value: data.metadata.nw_info_operator_sub1 },
                { label: "OEM", value: data.metadata.device_info_oem },
                { label: "HW Type", value: data.metadata.device_info_hw_type },
                { label: "Chipset", value: data.metadata.device_info_chipset_id },
                { label: "APSS", value: data.metadata.apss_info_build_id },
                { label: "MPSS", value: data.metadata.mpss_info_build_id },
                { 
                    label: "Data Stall Count", 
                    value: data.dataStallCount ? data.dataStallCount.dataStallCount : "-"
                }
            ];
            return <DeviceInfoTable deviceData={deviceData} />;
        }

        // Show empty table structure when no device selected
        const emptyDeviceData = [
            { label: "System ID", value: "-" },
            { label: "Make", value: "-" },
            { label: "Model", value: "-" },
            { label: "Operator", value: "-" },
            { label: "OEM", value: "-" },
            { label: "HW Type", value: "-" },
            { label: "Chipset", value: "-" },
            { label: "APSS", value: "-" },
            { label: "MPSS", value: "-" }
            // Removed "Campaign ID"
        ];
        return <DeviceInfoTable deviceData={emptyDeviceData} />;
    };

    // Helper function to render device info pie charts
    const renderDeviceInfoCharts = () => {
        const isDeviceInfoLoading = isSectionLoading('deviceInfo', loadingStates);
        
        if (isDeviceInfoLoading) {
            return (
                <div className="charts-placeholder">
                    <PieChartSkeleton />
                    <PieChartSkeleton />
                    <PieChartSkeleton />
                </div>
            );
        }

        if (data?.deviceInfo) {
            return (
                <div className="charts-placeholder">
                    <CompactPieChart
                        title="License SN Distribution"
                        data={data.deviceInfo.licenseSN?.data || []}
                        labels={data.deviceInfo.licenseSN?.labels || []}
                        colors={data.deviceInfo.licenseSN?.colors || []}
                    />
                    <CompactPieChart
                        title="RAT Distribution"
                        data={data.deviceInfo.ratDistribution?.data || []}
                        labels={data.deviceInfo.ratDistribution?.labels || []}
                        colors={data.deviceInfo.ratDistribution?.colors || []}
                    />
                    <CompactPieChart
                        title="Flash Memory Partitions"
                        data={data.deviceInfo.flashMemoryPartitions?.data || []}
                        labels={data.deviceInfo.flashMemoryPartitions?.labels || []}
                        colors={data.deviceInfo.flashMemoryPartitions?.colors || []}
                        countUnit="MB"
                    />
                </div>
            );
        }

        // Show empty pie charts when no device selected
        const emptyData = { data: [], labels: [], colors: [] };
        return (
            <div className="charts-placeholder">
                <CompactPieChart
                    title="License SN Distribution"
                    data={emptyData.data}
                    labels={emptyData.labels}
                    colors={emptyData.colors}
                />
                <CompactPieChart
                    title="RAT Distribution"
                    data={emptyData.data}
                    labels={emptyData.labels}
                    colors={emptyData.colors}
                />
                <CompactPieChart
                    title="Flash Memory Partitions"
                    data={emptyData.data}
                    labels={emptyData.labels}
                    colors={emptyData.colors}
                    countUnit="MB"
                />
            </div>
        );
    };

    const getNetworkHealthFreshnessText = () => {
        const freshness = data?.networkHealth?.freshness;
        if (!freshness) {
            return '';
        }

        const latestDate = freshness.latestSnapshotDateUtc;
        const rowsFound = freshness.rowsFound ?? 0;
        const ageDays = typeof freshness.dataAgeDays === 'number' ? freshness.dataAgeDays : null;
        const pipelineCoverageEndUtc = freshness.pipelineCoverageEndUtc;

        const formatUtcDate = (isoLikeDate, isDateOnly = false) => {
            if (!isoLikeDate) return '';
            if (isDateOnly) {
                const date = new Date(`${isoLikeDate}T00:00:00Z`);
                return new Intl.DateTimeFormat(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    timeZone: 'UTC'
                }).format(date);
            }
            const date = new Date(isoLikeDate);
            return new Intl.DateTimeFormat(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC'
            }).format(date);
        };

        let dataUpdatedText = 'data update status unavailable';
        if (pipelineCoverageEndUtc) {
            const d = new Date(pipelineCoverageEndUtc);
            d.setUTCDate(d.getUTCDate() - 1);
            dataUpdatedText = `data updated through ${formatUtcDate(d.toISOString(), false)} UTC`;
        }

        if (!latestDate || rowsFound === 0) {
            return `Freshness: no device data in selected range; ${dataUpdatedText}`;
        }

        let ageText = 'age unknown';
        if (ageDays === 0) {
            ageText = 'updated today (UTC)';
        } else if (ageDays === 1) {
            ageText = '1 day old';
        } else if (ageDays > 1) {
            ageText = `${ageDays} days old`;
        }

        const latestDisplay = formatUtcDate(latestDate, true);

        return `Freshness: device last reported ${latestDisplay} UTC (${ageText}); ${dataUpdatedText}`;
    };

    const getDeviceInfoFreshnessText = () => {
        const freshness = data?.deviceInfo?.freshness;
        if (!freshness) {
            return '';
        }

        const latestDate = freshness.latestSnapshotDateUtc;
        const rowsFound = freshness.rowsFound ?? 0;
        const ageDays = typeof freshness.dataAgeDays === 'number' ? freshness.dataAgeDays : null;
        const updatedThrough = freshness.dataUpdatedThroughUtc;

        const formatUtcDate = (isoLikeDate, isDateOnly = false) => {
            if (!isoLikeDate) return '';
            if (isDateOnly) {
                const date = new Date(`${isoLikeDate}T00:00:00Z`);
                return new Intl.DateTimeFormat(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    timeZone: 'UTC'
                }).format(date);
            }
            const date = new Date(isoLikeDate);
            return new Intl.DateTimeFormat(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC'
            }).format(date);
        };

        let updatedText = 'data update status unavailable';
        if (updatedThrough) {
            const d = new Date(updatedThrough);
            d.setUTCDate(d.getUTCDate() - 1);
            updatedText = `data updated through ${formatUtcDate(d.toISOString(), false)} UTC`;
        }

        if (!latestDate || rowsFound === 0) {
            return `Freshness: no device data in selected range; ${updatedText}`;
        }

        let ageText = 'age unknown';
        if (ageDays === 0) {
            ageText = 'updated today (UTC)';
        } else if (ageDays === 1) {
            ageText = '1 day old';
        } else if (ageDays > 1) {
            ageText = `${ageDays} days old`;
        }

        return `Freshness: device last reported ${formatUtcDate(latestDate, true)} UTC (${ageText}); ${updatedText}`;
    };

    const getDeviceHealthFreshnessText = () => {
        const freshness = data?.deviceHealth?.freshness;
        if (!freshness) {
            return '';
        }

        const latestDate = freshness.latestSnapshotDateUtc;
        const rowsFound = freshness.rowsFound ?? 0;
        const ageDays = typeof freshness.dataAgeDays === 'number' ? freshness.dataAgeDays : null;
        const updatedThrough = freshness.dataUpdatedThroughUtc;

        const formatUtcDate = (isoLikeDate, isDateOnly = false) => {
            if (!isoLikeDate) return '';
            if (isDateOnly) {
                const date = new Date(`${isoLikeDate}T00:00:00Z`);
                return new Intl.DateTimeFormat(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    timeZone: 'UTC'
                }).format(date);
            }
            const date = new Date(isoLikeDate);
            return new Intl.DateTimeFormat(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC'
            }).format(date);
        };

        let updatedText = 'data update status unavailable';
        if (updatedThrough) {
            const d = new Date(updatedThrough);
            d.setUTCDate(d.getUTCDate() - 1);
            updatedText = `data updated through ${formatUtcDate(d.toISOString(), false)} UTC`;
        }

        if (!latestDate || rowsFound === 0) {
            return `Freshness: no device data in selected range; ${updatedText}`;
        }

        let ageText = 'age unknown';
        if (ageDays === 0) {
            ageText = 'updated today (UTC)';
        } else if (ageDays === 1) {
            ageText = '1 day old';
        } else if (ageDays > 1) {
            ageText = `${ageDays} days old`;
        }

        return `Freshness: device last reported ${formatUtcDate(latestDate, true)} UTC (${ageText}); ${updatedText}`;
    };

    const getBootKpiFreshnessText = () => {
        const freshness = data?.bootKpi?.freshness;
        if (!freshness) {
            return '';
        }

        const latestDate = freshness.latestSnapshotDateUtc;
        const rowsFound = freshness.rowsFound ?? 0;
        const ageDays = typeof freshness.dataAgeDays === 'number' ? freshness.dataAgeDays : null;
        const pipelineCoverageEndUtc = freshness.pipelineCoverageEndUtc;

        const formatUtcDate = (isoLikeDate, isDateOnly = false) => {
            if (!isoLikeDate) return '';
            if (isDateOnly) {
                const date = new Date(`${isoLikeDate}T00:00:00Z`);
                return new Intl.DateTimeFormat(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    timeZone: 'UTC'
                }).format(date);
            }
            const date = new Date(isoLikeDate);
            return new Intl.DateTimeFormat(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC'
            }).format(date);
        };

        let dataUpdatedText = 'data update status unavailable';
        if (pipelineCoverageEndUtc) {
            const d = new Date(pipelineCoverageEndUtc);
            d.setUTCDate(d.getUTCDate() - 1);
            dataUpdatedText = `data updated through ${formatUtcDate(d.toISOString(), false)} UTC`;
        }

        if (!latestDate || rowsFound === 0) {
            return `Freshness: no device data in selected range; ${dataUpdatedText}`;
        }

        let ageText = 'age unknown';
        if (ageDays === 0) {
            ageText = 'updated today (UTC)';
        } else if (ageDays === 1) {
            ageText = '1 day old';
        } else if (ageDays > 1) {
            ageText = `${ageDays} days old`;
        }

        const latestDisplay = formatUtcDate(latestDate, true);
        return `Freshness: device last reported ${latestDisplay} UTC (${ageText}); ${dataUpdatedText}`;
    };

    return (
        <div className="device-profile-creator">
            {/* FilterBar with full functionality - sticky like aggregate view */}
            <div className={`device-profile-filter-bar ${!isFilterBarVisible ? 'hidden' : ''}`}>
                <div className="device-filter-bar">
                    <div className="device-filter-bar-left">
                        <FilterBar
                            searchProps={{
                                placeholder: "Search by System ID",
                                onSearch: handleSearch,
                                value: searchTerm,
                                recentDevices: recentDevices,
                                onSelectRecent: (deviceId) => handleSearch(deviceId),
                                suggestions: filterState.filteredDeviceIds,
                                disabled: loading  // Disable search during loading
                            }}
                            timeRangeProps={{
                                selectedRange: selectedTimeRange,
                                onRangeChange: handleTimeRangeChange,
                                disabled: loading || (!currentDeviceId && filterState.filteredDeviceIds.length === 0),  // Disable during loading
                                onRefresh: currentDeviceId ? handleRefresh : null,
                                isRefreshing: loading,  // Use loading state directly
                                preset: "device"
                            }}
                            filterState={filterState}
                            onFilterChange={handleFilterChange}
                        />
                    </div>
                </div>
            </div>

            <div className="reports-container">
                    <div ref={deviceInfoCardRef}>
                        <Section 
                            title="Device Information" 
                            initiallyCollapsed={true}
                            headerMeta={getDeviceInfoFreshnessText()}
                        >
                        <div className="device-info-section">
                            <div className="device-info-left">
                                {renderDeviceInfoTable()}
                            </div>
                            <div className="device-info-right">
                                {renderDeviceInfoCharts()}
                            </div>
                        </div>
                        </Section>
                    </div>

                    <Section
                        title="Network Health"
                        initiallyCollapsed={true}
                        headerMeta={getNetworkHealthFreshnessText()}
                    >
                        {isSectionLoading('networkHealth', loadingStates) ? (
                            <div>
                                <div className="network-health-pie-charts">
                                    <PieChartSkeleton />
                                    <PieChartSkeleton />
                                    <PieChartSkeleton />
                                    <PieChartSkeleton />
                                </div>
                                <div className="network-health-events">
                                    <EventTimeSeriesSkeleton />
                                    <EventTimeSeriesSkeleton />
                                </div>
                                <div className="network-health-peak-rate">
                                    <TimeSeriesChartSkeleton />
                                    <TimeSeriesChartSkeleton />
                                </div>
                                <div className="network-health-signal-histograms">
                                    <HistogramChartSkeleton />
                                    <HistogramChartSkeleton />
                                    <HistogramChartSkeleton />
                                </div>
                                <div className="network-health-anomaly-grid">
                                    <AnomalyScatterSkeleton />
                                </div>
                                <div className="network-health-stats-grid">
                                    <StatsCardSkeleton />
                                    <StatsCardSkeleton />
                                    <StatsCardSkeleton />
                                </div>
                            </div>
                        ) : data?.networkHealth?.error ? (
                            <div className="section-error">
                                <div className="error-message">
                                    <h3>⚠️ {data.networkHealth.message || "Failed to load Network Health data"}</h3>
                                    <p>{data.networkHealth.details || "Please check your connection and try again"}</p>
                                    <button onClick={() => retrySection('networkHealth')} className="retry-button">
                                        Retry Network Health
                                    </button>
                                </div>
                            </div>
                        ) : data?.networkHealth ? (
                            <div>
                                {/* Top section: Network Status Pie Charts - Expanded to 6 charts */}
                                {/* Registration Status Section */}
                                <div className="network-health-registration-section">
                                    <div className="network-health-pie-charts">
                                        <CompactPieChart
                                            title="NAS Registration Status"
                                            data={data.networkHealth.registrationStatus.data}
                                            labels={data.networkHealth.registrationStatus.labels}
                                            colors={data.networkHealth.registrationStatus.colors}
                                        />
                                        <CompactPieChart
                                            title="NAS Registration Failure Reason"
                                            data={data.networkHealth.registrationFailureReason.data}
                                            labels={data.networkHealth.registrationFailureReason.labels}
                                            colors={data.networkHealth.registrationFailureReason.colors}
                                        />
                                    </div>
                                    
                                    {/* Registration Status Events Timeline - Full Width */}
                                    <div className="network-health-registration-timeline">
                                        <EventTimeSeriesChart
                                            title="NAS Registration Events"
                                            eventSeries={data.networkHealth.registrationStatusEvents.eventSeries}
                                            timeRange={selectedTimeRange}
                                            variant="timeline"
                                        />
                                    </div>
                                </div>
                                
                                {/* PDN Status Section */}
                                <div className="network-health-pdn-section">
                                    <div className="network-health-pie-charts">
                                        <CompactPieChart
                                            title="PDN Status"
                                            data={data.networkHealth.pdnStatus.data}
                                            labels={data.networkHealth.pdnStatus.labels}
                                            colors={data.networkHealth.pdnStatus.colors}
                                        />
                                        <CompactPieChart
                                            title="PDN Down Reason Codes"
                                            data={data.networkHealth.pdnStatusDownReason.data}
                                            labels={data.networkHealth.pdnStatusDownReason.labels}
                                            colors={data.networkHealth.pdnStatusDownReason.colors}
                                        />
                                        <CompactPieChart
                                            title="PDN APN Type Distribution"
                                            data={data.networkHealth.pdnApnTypeDistribution?.data || []}
                                            labels={data.networkHealth.pdnApnTypeDistribution?.labels || []}
                                            colors={data.networkHealth.pdnApnTypeDistribution?.colors || []}
                                        />
                                    </div>

                                    {/* PDN Status Events Timeline - Full Width */}
                                    <div className="network-health-pdn-timeline">
                                        <EventTimeSeriesChart
                                            title="PDN Status Events"
                                            eventSeries={data.networkHealth.pdnStatusEvents.eventSeries}
                                            timeRange={selectedTimeRange}
                                            variant="timeline"
                                        />
                                    </div>
                                </div>
                                
                                {/* Data Stall Section */}
                                <div className="network-health-data-stall-section">
                                    <div className="network-health-pie-charts">
                                        <CompactPieChart
                                            title="Data Stall Subsystem"
                                            data={data.networkHealth.dataStallSubsystem?.data || []}
                                            labels={data.networkHealth.dataStallSubsystem?.labels || []}
                                            colors={data.networkHealth.dataStallSubsystem?.colors || []}
                                        />
                                        <CompactPieChart
                                            title="Data Stall Recovery Actions"
                                            data={data.networkHealth.dataStallRecoveryActions?.data || []}
                                            labels={data.networkHealth.dataStallRecoveryActions?.labels || []}
                                            colors={data.networkHealth.dataStallRecoveryActions?.colors || []}
                                        />
                                    </div>
                                </div>
                                
                                {/* Data Stall Timeline section */}
                                <div className="network-health-data-stall-timeline">
                                    <EventTimeSeriesChart
                                        title="Data Stall Events"
                                        eventSeries={data.networkHealth.dataStallTimeline?.eventSeries || []}
                                        timeRange={selectedTimeRange}
                                        variant="timeline"
                                    />
                                </div>
                                
                                
                                {/* Peak Rate section - separate uplink and downlink */}
                                <div className="network-health-peak-rate">
                                    <TimeSeriesChart
                                        title="Peak Rate - Uplink"
                                        series={data.networkHealth.peakRate.series.filter(s => s.name === 'Uplink')}
                                        yAxisLabel="Mb/s"
                                        timeRange={selectedTimeRange}
                                    />
                                    <TimeSeriesChart
                                        title="Peak Rate - Downlink"
                                        series={data.networkHealth.peakRate.series.filter(s => s.name === 'Downlink')}
                                        yAxisLabel="Mb/s"
                                        timeRange={selectedTimeRange}
                                    />
                                </div>
                                
                                {/* Anomaly Statistics section - moved above histograms */}
                                <div className="network-health-stats-grid">
                                    <StatsCard
                                        title="RSRP Anomalies"
                                        totalLabel="Total Instances"
                                        totalValue={data.networkHealth.rsrpStats.total}
                                        anomalousLabel="Anomalous Instances"
                                        anomalousValue={data.networkHealth.rsrpStats.anomalous}
                                        percentageLabel="Percentage"
                                        percentageValue={data.networkHealth.rsrpStats.percentage}
                                        unit="dBm"
                                    />
                                    <StatsCard
                                        title="RSRQ Anomalies"
                                        totalLabel="Total Instances"
                                        totalValue={data.networkHealth.rsrqStats.total}
                                        anomalousLabel="Anomalous Instances"
                                        anomalousValue={data.networkHealth.rsrqStats.anomalous}
                                        percentageLabel="Percentage"
                                        percentageValue={data.networkHealth.rsrqStats.percentage}
                                        unit="dB"
                                    />
                                    <StatsCard
                                        title="SINR Anomalies"
                                        totalLabel="Total Instances"
                                        totalValue={data.networkHealth.sinrStats.total}
                                        anomalousLabel="Anomalous Instances"
                                        anomalousValue={data.networkHealth.sinrStats.anomalous}
                                        percentageLabel="Percentage"
                                        percentageValue={data.networkHealth.sinrStats.percentage}
                                        unit="dB"
                                    />
                                </div>
                                
                                {/* Signal Strength histograms - 3 equal width columns */}
                                <div className="network-health-signal-histograms">
                                    <HistogramChart
                                        title="Signal Strength (RSRP)"
                                        bins={data.networkHealth.rsrpHistogram.bins}
                                        xAxisLabel="RSRP (dBm)"
                                        yAxisLabel="Number of Instances"
                                        color="#3498db"
                                    />
                                    <HistogramChart
                                        title="Signal Strength (RSRQ)"
                                        bins={data.networkHealth.rsrqHistogram.bins}
                                        xAxisLabel="RSRQ (dB)"
                                        yAxisLabel="Number of Instances"
                                        color="#e74c3c"
                                    />
                                    <HistogramChart
                                        title="Signal Strength (SINR)"
                                        bins={data.networkHealth.sinrHistogram.bins}
                                        xAxisLabel="SINR (dB)"
                                        yAxisLabel="Number of Instances"
                                        color="#2ecc71"
                                    />
                                </div>
                                
                                {/* Anomaly Detection section */}
                                <div className="network-health-anomaly-grid">
                                    <AnomalyScatterChart
                                        title="Anomalous Signal Values"
                                        anomalyData={data.networkHealth.anomalousSignalValues}
                                        yAxisLabel="Signal Strength"
                                        timeRange={selectedTimeRange}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div>
                                {/* Registration Status Section - Empty State */}
                                <div className="network-health-registration-section">
                                    <div className="network-health-pie-charts">
                                        <CompactPieChart
                                            title="NAS Registration Status"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                        <CompactPieChart
                                            title="NAS Registration Failure Reason"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                    </div>
                                    
                                    {/* Registration Status Events Timeline - Empty State */}
                                    <div className="network-health-registration-timeline">
                                        <EventTimeSeriesChart
                                            title="NAS Registration Events"
                                            eventSeries={[]}
                                            timeRange={selectedTimeRange}
                                            variant="timeline"
                                        />
                                    </div>
                                </div>
                                
                                {/* PDN Status Section - Empty State */}
                                <div className="network-health-pdn-section">
                                    <div className="network-health-pie-charts">
                                        <CompactPieChart
                                            title="PDN Status"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                        <CompactPieChart
                                            title="PDN Down Reason Codes"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                        <CompactPieChart
                                            title="PDN APN Type Distribution"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                    </div>

                                    {/* PDN Status Events Timeline - Empty State */}
                                    <div className="network-health-pdn-timeline">
                                        <EventTimeSeriesChart
                                            title="PDN Status Events"
                                            eventSeries={[]}
                                            timeRange={selectedTimeRange}
                                            variant="timeline"
                                        />
                                    </div>
                                </div>
                                
                                {/* Data Stall Section - Empty State */}
                                <div className="network-health-data-stall-section">
                                    <div className="network-health-pie-charts">
                                        <CompactPieChart
                                            title="Data Stall Subsystem"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                        <CompactPieChart
                                            title="Data Stall Recovery Actions"
                                            data={[]}
                                            labels={[]}
                                            colors={[]}
                                        />
                                    </div>
                                </div>
                                
                                {/* Data Stall Timeline section - empty state */}
                                <div className="network-health-data-stall-timeline">
                                    <EventTimeSeriesChart
                                        title="Data Stall Events"
                                        eventSeries={[]}
                                        timeRange={selectedTimeRange}
                                        variant="timeline"
                                    />
                                </div>
                                
                                {/* Peak Rate section - empty state */}
                                <div className="network-health-peak-rate">
                                    <TimeSeriesChart
                                        title="Peak Rate - Uplink"
                                        series={[]}
                                        yAxisLabel="Mb/s"
                                        timeRange={selectedTimeRange}
                                    />
                                    <TimeSeriesChart
                                        title="Peak Rate - Downlink"
                                        series={[]}
                                        yAxisLabel="Mb/s"
                                        timeRange={selectedTimeRange}
                                    />
                                </div>
                                
                                {/* Anomaly Statistics section - moved above histograms */}
                                <div className="network-health-stats-grid">
                                    <StatsCard
                                        title="RSRP Anomalies"
                                        totalLabel="Total Instances"
                                        totalValue={0}
                                        anomalousLabel="Anomalous Instances"
                                        anomalousValue={0}
                                        percentageLabel="Percentage"
                                        percentageValue={0}
                                        unit="dBm"
                                    />
                                    <StatsCard
                                        title="RSRQ Anomalies"
                                        totalLabel="Total Instances"
                                        totalValue={0}
                                        anomalousLabel="Anomalous Instances"
                                        anomalousValue={0}
                                        percentageLabel="Percentage"
                                        percentageValue={0}
                                        unit="dB"
                                    />
                                    <StatsCard
                                        title="SINR Anomalies"
                                        totalLabel="Total Instances"
                                        totalValue={0}
                                        anomalousLabel="Anomalous Instances"
                                        anomalousValue={0}
                                        percentageLabel="Percentage"
                                        percentageValue={0}
                                        unit="dB"
                                    />
                                </div>
                                
                                {/* Signal Strength histograms - empty state */}
                                <div className="network-health-signal-histograms">
                                    <HistogramChart
                                        title="Signal Strength (RSRP)"
                                        bins={[]}
                                        xAxisLabel="RSRP (dBm)"
                                        yAxisLabel="Number of Instances"
                                        color="#3498db"
                                    />
                                    <HistogramChart
                                        title="Signal Strength (RSRQ)"
                                        bins={[]}
                                        xAxisLabel="RSRQ (dB)"
                                        yAxisLabel="Number of Instances"
                                        color="#e74c3c"
                                    />
                                    <HistogramChart
                                        title="Signal Strength (SINR)"
                                        bins={[]}
                                        xAxisLabel="SINR (dB)"
                                        yAxisLabel="Number of Instances"
                                        color="#2ecc71"
                                    />
                                </div>
                                
                                <div className="network-health-anomaly-grid">
                                    <AnomalyScatterChart
                                        title="Anomalous Signal Values"
                                        anomalyData={[]}
                                        yAxisLabel="Signal Strength"
                                        timeRange={selectedTimeRange}
                                    />
                                </div>
                            </div>
                        )}
                    </Section>

                    <Section title="Device Health" initiallyCollapsed={true} headerMeta={getDeviceHealthFreshnessText()}>
                        {isSectionLoading('deviceHealth', loadingStates) ? (
                            <div>
                                <TimeSeriesChartSkeleton />
                                <TimeSeriesChartSkeleton />
                                <TimeSeriesChartSkeleton />
                                <TimeSeriesChartSkeleton />
                                <div>
                                    <PieChartSkeleton />
                                    <EventTimeSeriesSkeleton />
                                </div>
                            </div>
                        ) : data?.deviceHealth?.error ? (
                            <div className="section-error">
                                <div className="error-message">
                                    <h3>⚠️ {data.deviceHealth.message}</h3>
                                    <p>{data.deviceHealth.details}</p>
                                    <button onClick={() => retrySection('deviceHealth')} className="retry-button">
                                        Retry Device Health
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                    <TimeSeriesChart
                                        title="Thermal Trends"
                                        series={data?.deviceHealth?.thermalData?.series || []}
                                        yAxisLabel="Temperature (°C)"
                                        timeRange={selectedTimeRange}
                                    />

                                    <TimeSeriesChart
                                        title="Flash Usage"
                                        series={data?.deviceHealth?.flashUsage?.series || []}
                                        yAxisLabel="Memory (MB)"
                                        timeRange={selectedTimeRange}
                                    />

                                    <TimeSeriesChart
                                        title="Memory Usage"
                                        series={data?.deviceHealth?.memoryUsage?.series || []}
                                        yAxisLabel="Memory (MB)"
                                        timeRange={selectedTimeRange}
                                    />

                                    <TimeSeriesChart
                                        title="CPU Usage"
                                        series={data?.deviceHealth?.cpuUsage?.series || []}
                                        yAxisLabel="Usage (%)"
                                        timeRange={selectedTimeRange}
                                    />

                                    <div>
                                        <CompactPieChart
                                            title="Subsystem Restart Events"
                                            data={data?.deviceHealth?.subsystemRestartEvents?.data || []}
                                            labels={data?.deviceHealth?.subsystemRestartEvents?.labels || []}
                                            colors={data?.deviceHealth?.subsystemRestartEvents?.colors || []}
                                        />
                                        <EventTimeSeriesChart
                                            title="Subsystem Restarts"
                                            eventSeries={data?.deviceHealth?.subsystemRestartTimeline?.eventSeries || []}
                                            timeRange={selectedTimeRange}
                                            variant="timeline"
                                        />
                                    </div>
                            </div>
                        )}
                    </Section>

                    <Section title="Boot KPI" initiallyCollapsed={true} headerMeta={getBootKpiFreshnessText()}>
                        {isSectionLoading('bootKpi', loadingStates) ? (
                            <div>
                                <div className="boot-kpi-charts">
                                    <PieChartSkeleton />
                                    <HistogramChartSkeleton />
                                </div>
                                <div className="boot-kpi-charts">
                                    <HistogramChartSkeleton />
                                    <HistogramChartSkeleton />
                                </div>
                                <div className="boot-kpi-charts">
                                    <HistogramChartSkeleton />
                                    <HistogramChartSkeleton />
                                </div>
                                <div className="boot-kpi-charts">
                                    <HistogramChartSkeleton />
                                    <HistogramChartSkeleton />
                                </div>
                                <div className="boot-kpi-charts">
                                    <HistogramChartSkeleton />
                                    <HistogramChartSkeleton />
                                </div>
                            </div>
                        ) : data?.bootKpi?.error ? (
                            <div className="section-error">
                                <div className="error-message">
                                    <h3>⚠️ {data.bootKpi.message || "Failed to load Boot KPI data"}</h3>
                                    <p>{data.bootKpi.details || "Please check your connection and try again"}</p>
                                    <button onClick={() => retrySection('bootKpi')} className="retry-button">
                                        Retry Boot KPI
                                    </button>
                                </div>
                            </div>
                        ) : data?.bootKpi ? (
                            <div>
                                {/* Boot KPI Type Pie Chart */}
                                <div className="boot-kpi-pie-row">
                                    <CompactPieChart
                                        title="Boot KPI Type"
                                        data={data.bootKpi.kpiDistribution.data}
                                        labels={data.bootKpi.kpiDistribution.labels}
                                        colors={data.bootKpi.kpiDistribution.colors}
                                    />
                                </div>
                                
                                {/* Boot KPI Timing Statistics Table */}
                                <BootKpiTable timingHistograms={data.bootKpi.timingHistograms} />
                            </div>
                        ) : (
                            <div>
                                {/* Empty state - Boot KPI Pie Chart */}
                                <div className="boot-kpi-pie-row">
                                    <CompactPieChart
                                        title="Boot KPI Type"
                                        data={[]}
                                        labels={[]}
                                        colors={[]}
                                    />
                                </div>
                                
                                {/* Empty state - Boot KPI Timing Statistics Table */}
                                <BootKpiTable timingHistograms={{}} />
                            </div>
                        )}
                    </Section>
            </div>
        </div>
    );
}

export default DeviceView;
